import type { ComponentProps } from "react";
import { useState } from "react";
import { useRef } from "react";
import { LuMic, LuMusic } from "react-icons/lu";

import { useEditorUpload } from "@/components/app/editor-v3/use-editor-upload";
import { VoiceRecorderOverlay } from "@/components/shared/voice-recorder-overlay";
import { Button } from "@/components/ui/button";
import {
  DropdownDrawer,
  DropdownDrawerContent,
  DropdownDrawerItem,
  DropdownDrawerTrigger,
} from "@/components/ui/dropdown-drawer";
import { Input } from "@/components/ui/input";
import {
  MAX_AUDIO_LENGTH_SECONDS,
  MAX_VIDEO_SIZE_BYTES,
} from "@/utils/constants";

import { EDITOR_MEDIA_ACCEPT, EditorMediaType } from "../constants";

export const EditorActionAudio = ({
  side,
  align,
  defaultAction = "menu",
  ...props
}: ComponentProps<typeof Button> &
  Pick<ComponentProps<typeof DropdownDrawerContent>, "side" | "align"> & {
    defaultAction?: "menu" | "file" | "record";
  }) => {
  const fileInputRef = useRef<HTMLInputElement>(null!);
  const { addFiles } = useEditorUpload();
  const [open, setOpen] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);

  const uploadAudioFile = () => {
    fileInputRef.current.click();
  };

  const openVoiceRecorder = () => {
    setShowVoiceRecorder(true);
  };

  return (
    <>
      <Input
        className="hidden"
        type="file"
        accept={EDITOR_MEDIA_ACCEPT.audio}
        multiple
        hidden
        ref={fileInputRef}
        onChange={async (ev) => {
          const files = ev.target.files;

          if (!files) return;

          addFiles(Array.from(files), EditorMediaType.Audio, {
            maxFileSize: MAX_VIDEO_SIZE_BYTES,
            maxLength: MAX_AUDIO_LENGTH_SECONDS,
          });

          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
        }}
      />

      {defaultAction === "menu" ? (
        <DropdownDrawer
          historyName="editor-audio-actions"
          open={open}
          onOpenChange={setOpen}
          title="Audio"
        >
          <DropdownDrawerTrigger
            render={
              <Button {...props}>
                <LuMusic className="text-lg" />
              </Button>
            }
          />
          <DropdownDrawerContent align={align} side={side}>
            <DropdownDrawerItem onClick={uploadAudioFile}>
              <LuMusic className="text-lg" />
              <span>Audio File</span>
            </DropdownDrawerItem>
            <DropdownDrawerItem onClick={openVoiceRecorder}>
              <LuMic className="text-lg" />
              <span>Voice Recording</span>
            </DropdownDrawerItem>
          </DropdownDrawerContent>
        </DropdownDrawer>
      ) : (
        <Button
          {...props}
          onClick={() => {
            if (defaultAction === "file") {
              uploadAudioFile();
            } else {
              openVoiceRecorder();
            }
          }}
        >
          {defaultAction === "record" ? (
            <LuMic className="text-lg" />
          ) : (
            <LuMusic className="text-lg" />
          )}
        </Button>
      )}

      {showVoiceRecorder && (
        <VoiceRecorderOverlay
          onClose={() => setShowVoiceRecorder(false)}
          onFinish={(file) => {
            addFiles([file], EditorMediaType.Audio, {
              maxFileSize: MAX_VIDEO_SIZE_BYTES,
              maxLength: MAX_AUDIO_LENGTH_SECONDS,
            });
          }}
        />
      )}
    </>
  );
};
